// apps/admin/src/features/billing/GatewayFeeForm.tsx
//
// Phase 8.2 Wave 2 Task 08 — Payment Gateway Fee Configuration form.
//
// Per CONTEXT.md / API-CONTRACTS.md §PATCH /api/v1/admin/payment-gateways/:id
//   - Payment Gateways platform-global config surface
//   - Fee model: flat / percentage / combined (vendor bears fee)
//
// Fee model contract (matches BE zod FeeConfig):
//   fee.flat_cents     — non-negative integer cents
//   fee.percent_bps    — basis points (0..10000); 250 = 2.50%
//
// UI exposes 3 modes via radio group:
//   - "flat"      — only flat_cents (percent_bps stays 0)
//   - "percent"   — only percent_bps (flat_cents stays 0)
//   - "combined"  — both flat_cents AND percent_bps
//
// Submit requires a `reason` field (≥5 chars per zod) — audit trail
//  immutability rule. PATCH returns `audit_log_id` so the
// page can render a toast linking to the audit log.
//
// Test IDs (from PLAN/task-08.md):
//   - gateway-fee-form
//
// vg-binding: PLAN:task-08
// vg-binding: P8.D-85
// vg-binding: P8.D-453
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
  formatUsdCents,
} from '@printway/ui';

import { ApiError, apiClient } from '../../lib/apiClient.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Types — mirror API-CONTRACTS.md zod schemas
// ---------------------------------------------------------------------------

export type FeeMode = 'flat' | 'percent' | 'combined';

export interface GatewayFeeConfig {
  flat_cents: number;
  percent_bps: number;
}

export interface GatewayPatchResponse {
  id: string;
  updated_at: string;
  audit_log_id: string;
}

export interface GatewayFeeFormProps {
  gatewayId: string;
  gatewayName: string;
  initialFee: GatewayFeeConfig;
  /** Optional callback fires after a successful PATCH lands. */
  onSaved?: (res: GatewayPatchResponse) => void;
}

// ---------------------------------------------------------------------------
// Helpers — derive mode from existing FeeConfig + UI ↔ cents/bps
// ---------------------------------------------------------------------------

function deriveMode(fee: GatewayFeeConfig): FeeMode {
  if (fee.flat_cents > 0 && fee.percent_bps > 0) return 'combined';
  if (fee.percent_bps > 0) return 'percent';
  return 'flat';
}

/** Parse free-form `$X.XX` → integer cents. Empty / NaN → 0. */
function parseDollars(input: string): number {
  const cleaned = input.replace(/[^0-9.]/g, '');
  if (!cleaned) return 0;
  const value = Number.parseFloat(cleaned);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.round(value * 100);
}

/** Parse free-form `Y.YY%` → integer basis points (1 bp = 0.01%). */
function parsePercent(input: string): number {
  const cleaned = input.replace(/[^0-9.]/g, '');
  if (!cleaned) return 0;
  const value = Number.parseFloat(cleaned);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(10000, Math.round(value * 100));
}

function centsToDollars(c: number): string {
  return (c / 100).toFixed(2);
}

function bpsToPercent(bps: number): string {
  return (bps / 100).toFixed(2);
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `fee-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GatewayFeeForm({
  gatewayId,
  gatewayName,
  initialFee,
  onSaved,
}: GatewayFeeFormProps): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const qc = useQueryClient();

  const [mode, setMode] = useState<FeeMode>(() => deriveMode(initialFee));
  const [flatStr, setFlatStr] = useState<string>(() =>
    centsToDollars(initialFee.flat_cents),
  );
  const [percentStr, setPercentStr] = useState<string>(() =>
    bpsToPercent(initialFee.percent_bps),
  );
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Whenever the upstream `initialFee` prop changes (e.g. list query
  // refetched after another admin's edit), reset local state so the
  // form no longer holds the stale draft.
  useEffect(() => {
    setMode(deriveMode(initialFee));
    setFlatStr(centsToDollars(initialFee.flat_cents));
    setPercentStr(bpsToPercent(initialFee.percent_bps));
    setReason('');
    setFieldError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value-keyed on flat_cents/percent_bps (all fields deriveMode reads); depending on the `initialFee` object identity would wipe the draft on every refetch returning identical values
  }, [initialFee.flat_cents, initialFee.percent_bps, gatewayId]);

  const patchMutation = useMutation<
    GatewayPatchResponse,
    Error,
    { fee: GatewayFeeConfig; reason: string }
  >({
    mutationFn: async (body) => {
      return apiClient.patch<GatewayPatchResponse>(
        `/api/v1/admin/payment-gateways/${encodeURIComponent(gatewayId)}`,
        body,
        { idempotencyKey: newIdempotencyKey() },
      );
    },
    onSuccess: (res) => {
      toast({
        variant: 'success',
        title: t('gateway.feeForm.toast.updatedTitle'),
        description: t('gateway.feeForm.toast.auditLog', { id: res.audit_log_id }),
      });
      setReason('');
      void qc.invalidateQueries({ queryKey: ['admin', 'payment-gateways'] });
      onSaved?.(res);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.fieldErrors) {
          const first = Object.values(err.fieldErrors)[0]?.[0];
          if (first) {
            setFieldError(first);
            return;
          }
        }
        toast({
          variant: 'error',
          title: t('gateway.feeForm.toast.saveFailedTitle'),
          description: err.message,
        });
        return;
      }
      toast({
        variant: 'error',
        title: t('gateway.feeForm.toast.saveFailedTitle'),
        description: err.message || t('gateway.feeForm.toast.networkError'),
      });
    },
  });

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setFieldError(null);

    if (reason.trim().length < 5) {
      setFieldError(t('gateway.feeForm.errors.reasonMin'));
      return;
    }

    const flatCents = mode === 'percent' ? 0 : parseDollars(flatStr);
    const percentBps = mode === 'flat' ? 0 : parsePercent(percentStr);

    if (mode !== 'percent' && flatCents < 0) {
      setFieldError(t('gateway.feeForm.errors.flatMin'));
      return;
    }
    if (mode !== 'flat' && (percentBps < 0 || percentBps > 10000)) {
      setFieldError(t('gateway.feeForm.errors.percentRange'));
      return;
    }
    if (mode === 'combined' && flatCents === 0 && percentBps === 0) {
      setFieldError(t('gateway.feeForm.errors.combinedBoth'));
      return;
    }
    if (mode === 'flat' && flatCents === 0) {
      setFieldError(t('gateway.feeForm.errors.flatRequired'));
      return;
    }
    if (mode === 'percent' && percentBps === 0) {
      setFieldError(t('gateway.feeForm.errors.percentRequired'));
      return;
    }

    patchMutation.mutate({
      fee: { flat_cents: flatCents, percent_bps: percentBps },
      reason: reason.trim(),
    });
  }

  // Preview the resulting effective fee in plain US-English copy.
  const previewFlat = mode === 'percent' ? 0 : parseDollars(flatStr);
  const previewBps = mode === 'flat' ? 0 : parsePercent(percentStr);
  const previewText =
    mode === 'flat'
      ? formatUsdCents(previewFlat)
      : mode === 'percent'
        ? `${bpsToPercent(previewBps)}%`
        : `${formatUsdCents(previewFlat)} + ${bpsToPercent(previewBps)}%`;

  return (
    <form
      data-testid="gateway-fee-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-lg border border-pw-border bg-pw-bg p-4"
    >
      <header>
        <h3 className="text-sm font-semibold text-pw-text">
          {t('gateway.feeForm.heading', { gateway: gatewayName })}
        </h3>
        <p className="mt-1 text-xs text-pw-text-muted">
          {t('gateway.feeForm.subtitle')}
        </p>
      </header>

      <div>
        <label
          htmlFor="gateway-fee-mode"
          className="block text-sm font-medium text-pw-text"
        >
          {t('gateway.feeForm.modeLabel')}
        </label>
        <Select
          value={mode}
          onValueChange={(value) => setMode(value as FeeMode)}
        >
          <SelectTrigger
            id="gateway-fee-mode"
            className="mt-1"
            data-testid="gateway-fee-mode"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="flat">{t('gateway.feeForm.mode.flat')}</SelectItem>
            <SelectItem value="percent">
              {t('gateway.feeForm.mode.percent')}
            </SelectItem>
            <SelectItem value="combined">
              {t('gateway.feeForm.mode.combined')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {mode !== 'percent' ? (
          <div>
            <label
              htmlFor="gateway-fee-flat"
              className="block text-sm font-medium text-pw-text"
            >
              {t('gateway.feeForm.flatLabel')}
            </label>
            <Input
              id="gateway-fee-flat"
              data-testid="gateway-fee-flat-input"
              type="text"
              inputMode="decimal"
              value={flatStr}
              onChange={(e) => setFlatStr(e.target.value)}
              placeholder="0.30"
              disabled={patchMutation.isPending}
            />
          </div>
        ) : null}
        {mode !== 'flat' ? (
          <div>
            <label
              htmlFor="gateway-fee-percent"
              className="block text-sm font-medium text-pw-text"
            >
              {t('gateway.feeForm.percentLabel')}
            </label>
            <Input
              id="gateway-fee-percent"
              data-testid="gateway-fee-percent-input"
              type="text"
              inputMode="decimal"
              value={percentStr}
              onChange={(e) => setPercentStr(e.target.value)}
              placeholder="2.90"
              disabled={patchMutation.isPending}
            />
          </div>
        ) : null}
      </div>

      <p
        className="text-xs text-pw-text-muted"
        data-testid="gateway-fee-preview"
      >
        {t('gateway.feeForm.previewLabel')}{' '}
        <span className="font-mono text-pw-text">{previewText}</span>
      </p>

      <div>
        <label
          htmlFor="gateway-fee-reason"
          className="block text-sm font-medium text-pw-text"
        >
          {t('gateway.feeForm.reasonLabel')} <span className="text-red-600 dark:text-red-300">*</span>
        </label>
        <Input
          id="gateway-fee-reason"
          data-testid="gateway-fee-reason-input"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('gateway.feeForm.reasonPlaceholder')}
          disabled={patchMutation.isPending}
        />
      </div>

      {fieldError ? (
        <p
          role="alert"
          className="text-xs font-medium text-red-600 dark:text-red-300"
          data-testid="gateway-fee-error"
        >
          {fieldError}
        </p>
      ) : null}

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={patchMutation.isPending}
          data-testid="gateway-fee-submit-btn"
        >
          {patchMutation.isPending
            ? t('gateway.feeForm.saving')
            : t('gateway.feeForm.submit')}
        </Button>
      </div>
    </form>
  );
}

export default GatewayFeeForm;
